import { cn, formatCurrency } from '@/lib/utils'
import { Badge } from '@/components/ui'
import { Globe, ShieldCheck, CreditCard, Target, Sparkles, TrendingUp } from 'lucide-react'

interface CreditProfileSnippetProps {
  customerName: string
  country: string
  timestamp: string
  finalScore: number
  riskCategory: string
  customerSegment: string
  creditLimit: number
  shadowLimit: number
  maxTicketSize: number
  className?: string
}

export function CreditProfileSnippet({
  customerName,
  country,
  timestamp,
  finalScore,
  riskCategory,
  customerSegment,
  creditLimit,
  shadowLimit,
  maxTicketSize,
  className,
}: CreditProfileSnippetProps) {
  const getRiskBadgeVariant = (category: string) => {
    if (category.includes('High')) return 'danger'
    if (category.includes('Medium')) return 'warning'
    return 'success'
  }

  // Score color on 10-point scale (1-3 = low risk/good, 7-10 = high risk/bad)
  const getScoreColor = (score: number) => {
    if (score <= 3) return 'text-risk-excellent'  // Low risk
    if (score <= 5) return 'text-risk-good'       // Medium-low risk
    if (score <= 7) return 'text-risk-fair'       // Medium-high risk
    return 'text-risk-poor'                        // High risk
  }

  return (
    <div className={cn(
      'bg-gradient-to-br from-dashboard-navy via-dashboard-navy to-dashboard-purple rounded-xl overflow-hidden shadow-lg',
      className
    )}>
      {/* Decorative top bar */}
      <div className="h-1 bg-gradient-to-r from-dashboard-teal via-dashboard-purple to-risk-excellent" />
      
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white">{customerName}</h3>
              <Sparkles className="h-4 w-4 text-dashboard-teal" />
            </div>
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Globe className="h-3.5 w-3.5" />
              <span>{country}</span>
              <span className="text-white/40">•</span>
              <span>{timestamp}</span>
            </div>
          </div>
          <div className="text-right">
            <div className={cn('text-4xl font-bold', getScoreColor(finalScore))}>
              {Math.round(finalScore)}/10
            </div>
            <Badge 
              variant={getRiskBadgeVariant(riskCategory)} 
              className="mt-1 text-xs px-3"
            >
              {riskCategory}
            </Badge>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors">
            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
              <div className="p-1.5 bg-dashboard-purple/30 rounded-lg">
                <Target className="h-3.5 w-3.5 text-dashboard-purple" />
              </div>
              Customer Segment
            </div>
            <div className="text-lg font-bold text-white">{customerSegment}</div>
          </div>
          
          <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors">
            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
              <div className="p-1.5 bg-blue-500/30 rounded-lg">
                <CreditCard className="h-3.5 w-3.5 text-blue-400" />
              </div>
              Max Ticket Size
            </div>
            <div className="text-lg font-bold text-white">{formatCurrency(maxTicketSize)}</div>
          </div>
          
          <div className="bg-gradient-to-br from-risk-excellent/20 to-transparent backdrop-blur rounded-xl p-4 border border-risk-excellent/30">
            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
              <div className="p-1.5 bg-risk-excellent/30 rounded-lg">
                <ShieldCheck className="h-3.5 w-3.5 text-risk-excellent" />
              </div>
              Credit Limit
            </div>
            <div className="text-xl font-bold text-risk-excellent">{formatCurrency(creditLimit)}</div>
            <div className="text-[10px] text-white/40 mt-1">Recommended</div>
          </div>
          
          <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors">
            <div className="flex items-center gap-2 text-white/50 text-xs mb-2">
              <div className="p-1.5 bg-dashboard-teal/30 rounded-lg">
                <TrendingUp className="h-3.5 w-3.5 text-dashboard-teal" />
              </div>
              Shadow Limit
            </div>
            <div className="text-lg font-bold text-dashboard-teal">{formatCurrency(shadowLimit)}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
